import { Component, HostListener, Input, OnInit } from '@angular/core'
import { ActivatedRoute, RouterModule } from '@angular/router'
import { UserService } from '@app/core/_services/user.service'
import { firstValueFrom, forkJoin, Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'
import { User } from '@app/core/_models/user'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { BookSessionsService, BookSessionInfo } from '@app/core/_services/book-sessions.service'
import { faStar } from '@fortawesome/pro-solid-svg-icons'

import { BookingSource, SessionTileSource } from '@app/core/_services/analytics.service'
import { Title } from '@angular/platform-browser'
import { DialogService } from '@app/core/_services/dialog.service'
import { CancelSessionService, ConfirmedSessionsService } from '@app/core/_services'
import { UserAvailabiltyService } from '@app/core/_services/user-availabilty.service'
import {
    DURATION_FILTER_INCLUDE_25,
    DURATION_FILTER_INCLUDE_50,
    DURATION_FILTER_INCLUDE_75,
    DURATION_FILTER_INCLUDE_ALL,
} from '@app/shared-availability-list/components/availability-list-controls/availability-list-controls.component'
import { SESSION_STATE_DIRECT_BOOKER } from '@app/core/_services/session-state.service'
import { faCalendarExclamation, faCalendarStar } from '@fortawesome/pro-regular-svg-icons'
import {
    BookSessionPreferences,
    BookSessionsResponse,
    FavoritesAvailabilityResponse,
    SESSION_ACTIVITY_TYPE_ANYTHING,
    SessionActivityType,
} from '@app/core/_models'
import { faRefresh } from '@fortawesome/pro-solid-svg-icons'
import { SelectButtonChangeEvent, SelectButtonModule } from 'primeng/selectbutton'
import { ToggleSwitchChangeEvent, ToggleSwitchModule } from 'primeng/toggleswitch'
import { ContentStackService } from '@app/core/_services/content-stack.service'
import { ScrollPanelModule } from 'primeng/scrollpanel'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { CommonModule } from '@angular/common'
import { SelectModule } from 'primeng/select'
import { FormsModule } from '@angular/forms'
import { DividerModule } from 'primeng/divider'
import {
    AvailabilityListBookSession,
    AvailabilityListCancelSession,
    AvailabilityListItemComponent,
    AvailabilityListSelectedSession,
    AvailabilitySessionListItem,
} from './components/availability-list-item/availability-list-item.component'
import {
    FavoritesAvailabilityList,
    FavoritesAvailabilityListTimes,
    FavoritesListService,
} from './services/favorites-list.service'
import { MenuItem } from 'primeng/api'
import { BreadcrumbModule } from 'primeng/breadcrumb'

type AvailabilityListContentState =
    | 'loading'
    | 'emptyList'
    | 'validList'
    | 'noFavorites'
    | 'emptyForSelectedDuration'

interface DurationOption {
    labelDropdown: string
    labelButton: string
    value: number
}

const DURATION_OPTIONS: DurationOption[] = [
    { labelDropdown: 'All durations', labelButton: 'All', value: DURATION_FILTER_INCLUDE_ALL },
    { labelDropdown: '25 minutes', labelButton: '25m', value: DURATION_FILTER_INCLUDE_25 },
    { labelDropdown: '50 minutes', labelButton: '50m', value: DURATION_FILTER_INCLUDE_50 },
    { labelDropdown: '75 minutes', labelButton: '75m', value: DURATION_FILTER_INCLUDE_75 },
]

@Component({
    selector: 'fm-favorites-list',
    templateUrl: './favorites-list.component.html',
    styleUrls: ['./favorites-list.component.scss'],
    imports: [
        RouterModule,
        BreadcrumbModule,
        AvailabilityListItemComponent,
        CommonModule,
        FormsModule,
        FontAwesomeModule,
        SelectModule,
        SelectButtonModule,
        ToggleSwitchModule,
        ScrollPanelModule,
        DividerModule,
    ],
    standalone: true,
})
export class FavoritesListComponent implements OnInit {
    @Input() dynamicPanel: boolean = false

    public currentUser: User
    public listContentState: AvailabilityListContentState = 'loading'
    public filteredFavoritesSessionList: FavoritesAvailabilityList[] =
        new Array<FavoritesAvailabilityList>()
    public completeList: AvailabilitySessionListItem[] = []
    public hasFavorites: boolean
    public selectedSession: number = 0
    public selectedUserId: string
    public disableBookSessions: boolean = false
    public faStar = faStar
    public faRefresh = faRefresh
    public faCalendarStar = faCalendarStar
    public faCalendarExclamation = faCalendarExclamation
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    private favoritesAvailabilityResponse: FavoritesAvailabilityResponse
    public selectedQuietMode: boolean = false

    public startDate: moment
    public endDate: moment

    // Consolidate this when only the new layout is available
    public selectedOption = DURATION_FILTER_INCLUDE_ALL
    public durationOptions = DURATION_OPTIONS
    public selectedDurationOption = this.durationOptions[0]

    // Breadcrumbs
    breadcrumbItems: MenuItem[] = [
        { label: 'People', routerLink: '/people', queryParams: { type: 'favorites' } },
        { label: 'Favorites schedule' },
    ]

    constructor(
        public userService: UserService,
        private route: ActivatedRoute,
        private bookSessionService: BookSessionsService,
        private titleService: Title,
        private dialogService: DialogService,
        private confirmedSessionsService: ConfirmedSessionsService,
        private cancelSessionService: CancelSessionService,
        private userAvailabiltyService: UserAvailabiltyService,
        private favoritesListService: FavoritesListService,
        private contentStackService: ContentStackService,
    ) {}

    @HostListener('click')
    clicked() {
        this.selectedSession = 0
    }

    ngOnInit(): void {
        this.currentUser = this.route.snapshot.data['currentUser']
        if (this.currentUser) {
            this.continueInit()
        } else {
            this.userService.currentUser.pipe(take(1)).subscribe({
                next: (user) => {
                    this.currentUser = user
                    this.continueInit()
                },
            })
        }
    }

    private continueInit(): void {
        this.startDate = moment.tz(this.currentUser.timeZone).startOf('day')
        this.endDate = this.startDate.clone().add(14, 'days')
        this.titleService.setTitle("Favorites' availability - Focusmate")
        this.queryFavoritesAvailability()

        this.contentStackService.refresh$.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
            this.queryFavoritesAvailability()
        })
    }

    reloadSelect() {
        this.queryFavoritesAvailability()
    }

    onQuietModeToggleSwtichChange(event: ToggleSwitchChangeEvent): void {
        this.selectedQuietMode = event.checked
        if (this.listContentState !== 'loading') {
            this.populateSessionList()
        }
    }
    onQuietModeChanged(e) {
        this.selectedQuietMode = e
        if (this.listContentState !== 'loading') {
            this.populateSessionList()
        }
    }

    clearList() {
        this.completeList = this.favoritesListService.removeConfirmedSessionsFromList(
            this.completeList,
        )
        this.completeList = this.favoritesListService.removeAvailableSessionsFromList(
            this.completeList,
        )
        this.filteredFavoritesSessionList.length = 0
    }

    private queryFavoritesAvailability() {
        this.listContentState = 'loading'
        this.clearList()
        let start = this.startDate.valueOf()
        let end = this.endDate.valueOf()

        this.userAvailabiltyService.queryAvailableTimesWithFavorites(start, end).subscribe({
            next: (v) => this.receivedFavoriteUserAvailability(v),
            error: (e) => this.receivedFavoriteUserAvailabilityError(e),
        })
    }

    private receivedFavoriteUserAvailability(
        favoriteUsersAvailability: FavoritesAvailabilityResponse,
    ) {
        this.favoritesAvailabilityResponse = favoriteUsersAvailability
        this.favoritesListService.populateListFromFavoritesAvailabilityResponse(
            favoriteUsersAvailability,
            this.completeList,
            this.currentUser.timeZone,
        )
        this.hasFavorites = favoriteUsersAvailability.availability.favoritesCount > 0

        if (this.hasFavorites) {
            this.queryUserConfirmedSessions()
        } else {
            this.populateSessionList()
        }
    }

    private receivedFavoriteUserAvailabilityError(e) {}

    private queryUserConfirmedSessions() {
        this.confirmedSessionsService.getConfirmedSessions().subscribe({
            next: (v) => this.receivedConfirmedSessions(v),
            error: (e) => this.receivedConfirmedSessions([]),
        })
    }

    private receivedConfirmedSessions(confirmedSesions) {
        const start = this.startDate.valueOf()
        const end = this.endDate.valueOf()
        this.favoritesListService.populateListFromConfirmedSessionsResponse(
            confirmedSesions,
            this.completeList,
            start,
            end,
            this.currentUser.timeZone,
        )
        this.populateSessionList()
    }

    private populateSessionList() {
        this.favoritesListService.populateFilteredDisplayList(
            this.filteredFavoritesSessionList,
            this.completeList,
            this.currentUser.timeZone,
            this.selectedOption,
            this.selectedQuietMode,
        )
        this.setContentStateEmptyOrValid()
    }

    onDurationDropDownChanged(event: SelectButtonChangeEvent): void {
        this.selectedDurationOption = this.durationOptions.find(
            (element) => element.value == event.value,
        )
        if (this.selectedOption !== event.value) {
            this.selectedOption = event.value
            this.populateSessionList()
        }
    }

    durationFilterSelected(value: number): void {
        if (this.selectedOption !== value) {
            this.selectedOption = value
            this.populateSessionList()
        }
    }

    private setContentStateEmptyOrValid() {
        if (!this.hasFavorites) {
            this.listContentState = 'noFavorites'
        } else if (this.completeList.length === 0) {
            this.listContentState = 'emptyList'
        } else if (this.filteredFavoritesSessionList.length === 0) {
            this.listContentState = 'emptyForSelectedDuration'
        } else {
            this.listContentState = 'validList'
        }
    }

    public selectSession(sessionInfo: AvailabilityListSelectedSession) {
        this.selectedSession = sessionInfo.sessionTime
        this.selectedUserId = sessionInfo.userId
    }

    public bookSession(availabilityListBookSession: AvailabilityListBookSession) {
        const preferences: BookSessionPreferences = {
            favorites: { value: 'preferred' },
            quietMode: { value: this.selectedQuietMode },
        }
        let sessionTime = availabilityListBookSession.sessionTime
        let duration = availabilityListBookSession.duration
        let bookSessionInfo: BookSessionInfo = {
            sessionTime: sessionTime,
            userAvailabilityCode: '',
            duration: duration,
            userId: availabilityListBookSession.userId,
            title: '',
            preferences,
            activityType: SESSION_ACTIVITY_TYPE_ANYTHING,
        }
        this.disableBookSessions = true

        this.bookSessionService
            .bookSessions(
                [bookSessionInfo],
                this.currentUser.timeZone,
                BookingSource.ConsolidatedFavoritesList,
            )
            .pipe(take(1))
            .subscribe({
                next: (v) => this.receivedBookSessionResult(availabilityListBookSession, v),
                error: (e) => this.receivedBookSessionError(sessionTime, e),
                complete: () => this.receivedBookSessionComplete(sessionTime),
            })
    }

    public receivedBookSessionResult(
        availabilityListBookSession: AvailabilityListBookSession,
        result: BookSessionsResponse[],
    ) {
        let sessionTime = availabilityListBookSession.sessionTime
        let duration = availabilityListBookSession.duration
        let userId = availabilityListBookSession.userId
        let parsedRes = this.bookSessionService.parseBookResponse(result)

        if (parsedRes['numSuccess'] !== 0) {
            const sessionInfo = result.find((element) => element.sessionTime == sessionTime)
            this.updateSessionListWithBookedSession(sessionTime, userId, sessionInfo)
            this.dialogService.openConfirmTransaction('Session booked!')
        }

        if (parsedRes['planLimitReached'] === true) {
            this.dialogService.openPlanLimitReachedDialog()
        } else if (parsedRes['sessionConflict'] === true) {
            const conflictTimes = parsedRes['conflicts'].map((conflict) => conflict.sessionTime)
            this.dialogService
                .openSessionConflictWithCancel(conflictTimes, this.currentUser.timeZone)
                .afterClosed()
                .pipe(take(1))
                .subscribe((actionResult) => {
                    if (actionResult.action === 'cancelAndBook') {
                        const preferences: BookSessionPreferences = {
                            favorites: { value: 'preferred' },
                            quietMode: { value: this.selectedQuietMode },
                        }

                        let bookSessionInfo: BookSessionInfo = {
                            sessionTime: sessionTime,
                            userAvailabilityCode: '',
                            duration: duration,
                            userId: userId,
                            title: '',
                            preferences,
                            activityType: SESSION_ACTIVITY_TYPE_ANYTHING,
                        }
                        this.bookAndCancel(bookSessionInfo, parsedRes['conflicts'])
                    }
                })
        } else if (
            parsedRes['inviterNoLongerAvailable'] === true ||
            parsedRes['alreadyStarted'] === true
        ) {
            this.dialogService.openInviterMeetingNoLongerAvailable()
            this.queryFavoritesAvailability()
        }
    }

    public updateSessionListWithBookedSession(
        sessionTime: number,
        userId: string,
        sessionInfo: BookSessionsResponse,
    ) {
        /* Booking a session via availabiity list will hardcode activityType to anything */
        this.completeList.forEach((session) => {
            if (session.sessionTime === sessionTime && session.partnerUserId == userId) {
                session.externalId = sessionInfo.externalId
                session.confirmed = true
                session.actionComplete = true
                session.sessionState = SESSION_STATE_DIRECT_BOOKER
                session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
            }
        })

        this.filteredFavoritesSessionList.forEach((day) => {
            day.times.forEach((time) => {
                time.sessions25.forEach((session) => {
                    if (session.sessionTime === sessionTime && session.partnerUserId == userId) {
                        session.externalId = sessionInfo.externalId
                        session.confirmed = true
                        session.actionComplete = true
                        session.sessionState = SESSION_STATE_DIRECT_BOOKER
                        session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
                    }
                })
                time.sessions50.forEach((session) => {
                    if (session.sessionTime === sessionTime && session.partnerUserId == userId) {
                        session.externalId = sessionInfo.externalId
                        session.confirmed = true
                        session.actionComplete = true
                        session.sessionState = SESSION_STATE_DIRECT_BOOKER
                        session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
                    }
                })
                time.sessions75.forEach((session) => {
                    if (session.sessionTime === sessionTime && session.partnerUserId == userId) {
                        session.externalId = sessionInfo.externalId
                        session.confirmed = true
                        session.actionComplete = true
                        session.sessionState = SESSION_STATE_DIRECT_BOOKER
                        session.activityType = SESSION_ACTIVITY_TYPE_ANYTHING
                    }
                })
            })
        })
    }

    public receivedBookSessionError(sessionTime: number, e) {
        this.disableBookSessions = false
        this.updateActionComplete(true, sessionTime)
    }

    public receivedBookSessionComplete(sessionTime: number) {
        this.selectedSession = 0
        this.disableBookSessions = false
        this.updateActionComplete(true, sessionTime)
    }

    private bookAndCancel(
        bookInfo: BookSessionInfo,
        sessionsToCancel: { externalId: string; sessionTime: number }[],
    ) {
        let cancelRequests = []
        for (let { externalId, sessionTime } of sessionsToCancel) {
            cancelRequests.push(
                this.cancelSessionService.cancelSession(
                    externalId,
                    SessionTileSource.AvailabilityConflictModal,
                ),
            )
        }

        forkJoin(cancelRequests).subscribe({
            next: (v) => {
                for (let { externalId, sessionTime } of sessionsToCancel) {
                    this.updateSessionListWithCanceledSession(sessionTime)
                }
                this.bookSession(bookInfo)
            },
            error: (e) => {
                this.bookSession(bookInfo)
            },
        })
    }

    updateActionComplete(complete: boolean, sessionTime: number) {
        for (let day of this.filteredFavoritesSessionList) {
            for (let time of day.times) {
                for (let session of time.sessions25) {
                    if (session.sessionTime === sessionTime) {
                        session.actionComplete = complete
                        return
                    }
                }
                for (let session of time.sessions50) {
                    if (session.sessionTime === sessionTime) {
                        session.actionComplete = complete
                        return
                    }
                }
                for (let session of time.sessions75) {
                    if (session.sessionTime === sessionTime) {
                        session.actionComplete = complete
                        return
                    }
                }
            }
        }
    }

    cancelSession(availabilityListcancelSession: AvailabilityListCancelSession) {
        this.cancelSessionService
            .cancelSession(
                availabilityListcancelSession.externalId,
                SessionTileSource.ConsolidatedFavoritesList,
            )
            .subscribe({
                next: (v) =>
                    this.receivedCancelSessionSuccess(
                        availabilityListcancelSession.sessionTime,
                        availabilityListcancelSession.userId,
                    ),
                error: (e) => this.queryFavoritesAvailability(),
            })
    }

    private receivedCancelSessionSuccess(sessionTime: number, userId: string) {
        this.updateSessionListWithCanceledSession(sessionTime)

        this.dialogService.openConfirmTransaction('Session canceled')
    }

    private getOriginalActivityTypeFromList(
        availabiityList: FavoritesAvailabilityResponse,
        sessionTime: number,
    ): SessionActivityType | null {
        let session = availabiityList.availability.availableSessions.find(
            (element) => element.sessionTime == sessionTime,
        )
        return session?.activityType || null
    }

    private async updateSessionListWithCanceledSession(sessionTime: number) {
        /*
		Use Case 1: Session canceled had no partner, remove it from the list completely
		Use Case 2: Session canceled had a partner, but was not a favorite partner that
		            shares their calendar, remove it from the list completely
		Use Case 3: Session canceled was with a saved partner that is sharing their calendar
		            update state and keep in list
		
		NOTE: When canceling a session we must ensure to show the correct
		activity icon for the available session.
		Case 1: When we first loaded the list, this session was not booked and
		we have saved off the original activity type. Update it from our saved list
		Case 2: When we first loaded the list, this session was booked. In this case
		we do not know the activity type the partner had set, so querying it again and
		refresh the list
		*/
        let partnersActivityType = this.getOriginalActivityTypeFromList(
            this.favoritesAvailabilityResponse,
            sessionTime,
        )

        if (!partnersActivityType) {
            const start = this.startDate.valueOf()
            const end = this.endDate.valueOf()
            this.favoritesAvailabilityResponse = await firstValueFrom(
                this.userAvailabiltyService.queryAvailableTimesWithFavorites(start, end),
            )
            partnersActivityType = this.getOriginalActivityTypeFromList(
                this.favoritesAvailabilityResponse,
                sessionTime,
            )
        }

        for (let i = 0; i < this.completeList.length; ++i) {
            if (
                this.completeList[i].sessionTime === sessionTime &&
                this.completeList[i].confirmed
            ) {
                if (this.completeList[i].saved && this.completeList[i].sharedAvailability) {
                    this.completeList[i].confirmed = false
                    this.completeList[i].cancelConfirm = false
                    this.completeList[i].actionComplete = true
                    this.completeList[i].activityType = partnersActivityType
                    this.completeList[i].externalId = null
                } else {
                    this.completeList.splice(i, 1)
                }
                break
            }
        }

        let found = false
        for (let day = 0; day < this.filteredFavoritesSessionList.length; ++day) {
            for (let time = 0; time < this.filteredFavoritesSessionList[day].times.length; ++time) {
                if (this.filteredFavoritesSessionList[day].times[time].sessionTime == sessionTime) {
                    found = this.updateOrRemoveCanceledSession(
                        this.filteredFavoritesSessionList[day].times[time].sessions25,
                        partnersActivityType,
                    )
                    found =
                        found ||
                        this.updateOrRemoveCanceledSession(
                            this.filteredFavoritesSessionList[day].times[time].sessions50,
                            partnersActivityType,
                        )
                    found =
                        found ||
                        this.updateOrRemoveCanceledSession(
                            this.filteredFavoritesSessionList[day].times[time].sessions75,
                            partnersActivityType,
                        )

                    if (found) {
                        if (
                            this.isTimeNodeEmpty(this.filteredFavoritesSessionList[day].times[time])
                        ) {
                            this.filteredFavoritesSessionList[day].times.splice(time, 1)

                            if (this.isDayNodeEmpty(this.filteredFavoritesSessionList[day])) {
                                this.filteredFavoritesSessionList.splice(day, 1)
                            }
                        }
                        break
                    }
                }
            }
            if (found) {
                break
            }
        }
    }

    private isSavedWithSharedAvailabilityPartner(session: AvailabilitySessionListItem): boolean {
        return session.saved && session.sharedAvailability
    }

    private updateSessionToAvailable(
        session: AvailabilitySessionListItem,
        activityType: SessionActivityType,
    ): void {
        session.confirmed = false
        session.cancelConfirm = false
        session.actionComplete = true
        session.activityType = activityType
        session.externalId = null
    }

    private updateOrRemoveCanceledSession(
        sessionList: AvailabilitySessionListItem[],
        activityType: SessionActivityType,
    ): boolean {
        let found = false

        for (let session = 0; session < sessionList.length; ++session) {
            let checkSession = sessionList[session]
            if (checkSession.confirmed) {
                if (this.isSavedWithSharedAvailabilityPartner(checkSession)) {
                    this.updateSessionToAvailable(checkSession, activityType)
                    found = true
                    break
                } else {
                    sessionList.splice(session, 1)
                    found = true
                    break
                }
            }
        }
        return found
    }

    private isTimeNodeEmpty(node: FavoritesAvailabilityListTimes): boolean {
        if (
            node.sessions25.length === 0 &&
            node.sessions50.length === 0 &&
            node.sessions75.length === 0
        ) {
            return true
        } else {
            return false
        }
    }

    private isDayNodeEmpty(node: FavoritesAvailabilityList): boolean {
        if (node.times.length === 0) {
            return true
        } else {
            false
        }
    }

    dontPropagate(e: Event) {
        e.stopPropagation()
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
